import type { AdminQueryError } from "../../contracts/admin-query-errors";
import type { AdminQueryPort } from "../../contracts/admin-query-port";
import type {
  AdminDeviceKpiPage,
  AdminDeviceKpiQuery,
  AdminFailedFlowPage,
  AdminFailedFlowQuery,
  AdminIncidentLogArtifactAccessAction,
  AdminIncidentLogArtifactAccessGrant,
  AdminJobPage,
  AdminJobQuery,
  AdminMediaArtifactAccessAction,
  AdminMediaArtifactAccessGrant,
  AdminOverviewQuery,
  AdminOverviewSummary,
  AdminQueryEnvelope,
  AdminReleaseRegression,
  AdminReleaseRegressionQuery,
  AdminUserDataDetail,
  AdminUserLookupInput,
  AdminUserSummaryPage,
  AdminUserSummaryQuery,
  AdminUserSummary,
} from "../../contracts/admin-read-models";
import { normalizeAdminQueryError } from "./normalize-admin-query-error";
import { normalizeAdminUserSummaryRuntimePayload } from "./admin-user-summary-runtime-payload";

export type HttpAdminQueryAdapterConfig = {
  apiBaseUrl: string;
  fetchFn?: typeof fetch;
  getAccessToken?: () => Promise<string | undefined> | string | undefined;
};

type EnvelopeRequestOptions = {
  path: string;
  query?: Record<string, string | number | undefined>;
  normalizeData?: (data: unknown) => unknown;
};

type ErrorEnvelope = {
  error?: {
    code?: string;
    message?: string;
    requestId?: string;
  };
};

function encodePathSegment(value: string): string {
  return encodeURIComponent(value);
}

function appendQueryValue(searchParams: URLSearchParams, key: string, value: string | number | undefined): void {
  if (value === undefined) {
    return;
  }

  searchParams.set(key, String(value));
}

function buildRequestUrl(baseUrl: string, path: string, query?: Record<string, string | number | undefined>): string {
  const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const url = new URL(`${normalizedBaseUrl}${normalizedPath}`);

  if (query !== undefined) {
    for (const [key, value] of Object.entries(query)) {
      appendQueryValue(url.searchParams, key, value);
    }
  }

  return url.toString();
}

function toTimeRangeQuery(query: { timeRange: { from: string; to: string } }): Record<string, string> {
  return {
    from: query.timeRange.from,
    to: query.timeRange.to,
  };
}

function readErrorRequestId(body: unknown): string | undefined {
  if (typeof body !== "object" || body === null) {
    return undefined;
  }

  const envelope = body as ErrorEnvelope;
  return typeof envelope.error?.requestId === "string" ? envelope.error.requestId : undefined;
}

function isEnvelopeShape<T>(value: unknown): value is AdminQueryEnvelope<T> {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const candidate = value as Partial<AdminQueryEnvelope<T>>;
  return (
    "data" in candidate &&
    typeof candidate.meta?.requestId === "string" &&
    typeof candidate.meta?.generatedAt === "string" &&
    typeof candidate.meta?.sourceFreshness === "object" &&
    candidate.meta.sourceFreshness !== null
  );
}

async function readJsonBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type");

  if (contentType?.includes("application/json")) {
    return response.json();
  }

  const text = await response.text();
  return text.length > 0 ? text : undefined;
}

function isHtmlShellFallbackBody(body: unknown, contentType: string | null): body is string {
  if (!contentType?.includes("text/html") || typeof body !== "string") {
    return false;
  }

  const normalized = body.toLowerCase();
  return normalized.includes("<!doctype html") || normalized.includes("<div id=\"root\"");
}

function toInvalidLookupError(message: string): AdminQueryError {
  return {
    code: "ADMIN_QUERY_INVALID_FILTER",
    message,
  };
}

export function createHttpAdminQueryAdapter(config: HttpAdminQueryAdapterConfig): AdminQueryPort {
  const fetchFn = config.fetchFn ?? fetch;

  async function requestEnvelope<T>(
    options: EnvelopeRequestOptions,
  ): Promise<AdminQueryEnvelope<T>> {
    const token = await config.getAccessToken?.();
    const headers = new Headers({
      Accept: "application/json",
    });

    if (typeof token === "string" && token.length > 0) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const response = await fetchFn(buildRequestUrl(config.apiBaseUrl, options.path, options.query), {
      method: "GET",
      headers,
    });
    const body = await readJsonBody(response);

    if (response.ok && isHtmlShellFallbackBody(body, response.headers.get("content-type"))) {
      throw normalizeAdminQueryError({
        status: 503,
        body: {
          error: {
            code: "ROUTE_NOT_FOUND",
            message:
              "Admin API base URL resolved to a static HTML host. Set VITE_ADMIN_API_BASE_URL to the deployed API service root or provide a same-origin /api/admin/* proxy.",
          },
        },
        message: "Admin query request resolved to a static HTML shell.",
      });
    }

    if (!response.ok) {
      throw normalizeAdminQueryError({
        status: response.status,
        requestId: readErrorRequestId(body),
        body,
        message: `Admin query request failed with status ${response.status}.`,
      });
    }

    if (!isEnvelopeShape<T>(body)) {
      throw normalizeAdminQueryError({
        status: response.status,
        body,
        message: "Admin query response envelope is invalid.",
      });
    }

    if (options.normalizeData) {
      return {
        ...body,
        data: options.normalizeData(body.data) as T,
      };
    }

    return body;
  }

  return {
    async getOverviewSummary(
      query: AdminOverviewQuery,
    ): Promise<AdminQueryEnvelope<AdminOverviewSummary>> {
      return requestEnvelope<AdminOverviewSummary>({
        path: "/api/admin/overview",
        query: {
          ...toTimeRangeQuery(query),
          feature: query.feature,
          useCase: query.useCase,
        },
      });
    },
    async searchFailedFlows(
      query: AdminFailedFlowQuery,
    ): Promise<AdminQueryEnvelope<AdminFailedFlowPage>> {
      return requestEnvelope<AdminFailedFlowPage>({
        path: "/api/admin/failed-flows",
        query: {
          ...toTimeRangeQuery(query),
          feature: query.feature,
          useCase: query.useCase,
          errorCode: query.errorCode,
          statusCode: query.statusCode,
          requestId: query.requestId,
          correlationId: query.correlationId,
          userIdHash: query.userIdHash,
          petIdHash: query.petIdHash,
          release: query.release,
          appVersion: query.appVersion,
          platform: query.platform,
          deviceInstallId: query.deviceInstallId,
          cursor: query.cursor,
          limit: query.limit,
        },
      });
    },
    async searchDeviceKpis(
      query: AdminDeviceKpiQuery,
    ): Promise<AdminQueryEnvelope<AdminDeviceKpiPage>> {
      return requestEnvelope<AdminDeviceKpiPage>({
        path: "/api/admin/device-kpis",
        query: {
          ...toTimeRangeQuery(query),
          release: query.release,
          appVersion: query.appVersion,
          platform: query.platform,
          deviceInstallId: query.deviceInstallId,
          cursor: query.cursor,
          limit: query.limit,
        },
      });
    },
    async searchUserSummaries(
      query: AdminUserSummaryQuery,
    ): Promise<AdminQueryEnvelope<AdminUserSummaryPage>> {
      return requestEnvelope<AdminUserSummaryPage>({
        path: "/api/admin/user-summaries",
        query: {
          ...toTimeRangeQuery(query),
          cursor: query.cursor,
          limit: query.limit,
        },
        normalizeData: (data) => {
          if (typeof data !== "object" || data === null || !("items" in data) || !Array.isArray((data as { items?: unknown[] }).items)) {
            return data;
          }
          return {
            ...(data as Record<string, unknown>),
            items: (data as { items: Array<Record<string, unknown>> }).items.map((item) =>
              typeof item === "object" && item !== null ? normalizeAdminUserSummaryRuntimePayload(item) : item,
            ),
          };
        },
      });
    },
    async getUserSummary(
      input: AdminUserLookupInput,
    ): Promise<AdminQueryEnvelope<AdminUserSummary>> {
      if (typeof input.internalUserId === "string" && input.internalUserId.length > 0) {
        return requestEnvelope<AdminUserSummary>({
          path: `/api/admin/users/${encodePathSegment(input.internalUserId)}`,
          normalizeData: (data) =>
            typeof data === "object" && data !== null
              ? normalizeAdminUserSummaryRuntimePayload(data as Record<string, unknown>)
              : data,
        });
      }

      if (typeof input.userIdHash === "string" && input.userIdHash.length > 0) {
        return requestEnvelope<AdminUserSummary>({
          path: `/api/admin/users/by-hash/${encodePathSegment(input.userIdHash)}`,
          normalizeData: (data) =>
            typeof data === "object" && data !== null
              ? normalizeAdminUserSummaryRuntimePayload(data as Record<string, unknown>)
              : data,
        });
      }

      if (
        (typeof input.requestId === "string" && input.requestId.length > 0) ||
        (typeof input.correlationId === "string" && input.correlationId.length > 0)
      ) {
        return requestEnvelope<AdminUserSummary>({
          path: "/api/admin/users",
          query: {
            requestId: input.requestId,
            correlationId: input.correlationId,
          },
          normalizeData: (data) =>
            typeof data === "object" && data !== null
              ? normalizeAdminUserSummaryRuntimePayload(data as Record<string, unknown>)
              : data,
        });
      }

      throw toInvalidLookupError(
        "User summary lookup requires internalUserId, userIdHash, requestId, or correlationId.",
      );
    },
    async getUserDataDetail(
      input: AdminUserLookupInput,
    ): Promise<AdminQueryEnvelope<AdminUserDataDetail>> {
      if (typeof input.internalUserId === "string" && input.internalUserId.length > 0) {
        return requestEnvelope<AdminUserDataDetail>({
          path: `/api/admin/users/${encodePathSegment(input.internalUserId)}/data`,
        });
      }

      throw toInvalidLookupError(
        "User data detail lookup requires internalUserId in AD-P0-003.",
      );
    },
    async issueUserMediaArtifactAccess(input: {
      internalUserId: string;
      artifactId: string;
      action: AdminMediaArtifactAccessAction;
    }): Promise<AdminQueryEnvelope<AdminMediaArtifactAccessGrant>> {
      if (input.internalUserId.length === 0 || input.artifactId.length === 0) {
        throw toInvalidLookupError(
          "Admin media artifact access requires internalUserId and artifactId.",
        );
      }

      return requestEnvelope<AdminMediaArtifactAccessGrant>({
        path: `/api/admin/users/${encodePathSegment(input.internalUserId)}/media/${encodePathSegment(input.artifactId)}/access`,
        query: {
          action: input.action,
        },
      });
    },
    async issueUserIncidentLogArtifactAccess(input: {
      internalUserId: string;
      artifactId: string;
      action: AdminIncidentLogArtifactAccessAction;
    }): Promise<AdminQueryEnvelope<AdminIncidentLogArtifactAccessGrant>> {
      if (input.internalUserId.length === 0 || input.artifactId.length === 0) {
        throw toInvalidLookupError(
          "Admin incident log artifact access requires internalUserId and artifactId.",
        );
      }

      return requestEnvelope<AdminIncidentLogArtifactAccessGrant>({
        path: `/api/admin/users/${encodePathSegment(input.internalUserId)}/incidents/${encodePathSegment(input.artifactId)}/access`,
        query: {
          action: input.action,
        },
      });
    },
    async searchJobs(query: AdminJobQuery): Promise<AdminQueryEnvelope<AdminJobPage>> {
      return requestEnvelope<AdminJobPage>({
        path: "/api/admin/jobs",
        query: {
          ...toTimeRangeQuery(query),
          jobType: query.jobType,
          status: query.status,
          userIdHash: query.userIdHash,
          petIdHash: query.petIdHash,
          linkedFileId: query.linkedFileId,
          linkedRecordId: query.linkedRecordId,
          requestId: query.requestId,
          correlationId: query.correlationId,
          release: query.release,
          appVersion: query.appVersion,
          cursor: query.cursor,
          limit: query.limit,
        },
      });
    },
    async getReleaseRegression(
      query: AdminReleaseRegressionQuery,
    ): Promise<AdminQueryEnvelope<AdminReleaseRegression>> {
      return requestEnvelope<AdminReleaseRegression>({
        path: "/api/admin/releases",
        query: {
          ...toTimeRangeQuery(query),
          release: query.release,
          appVersion: query.appVersion,
          platform: query.platform,
          cursor: query.cursor,
          limit: query.limit,
        },
      });
    },
  };
}
